/**
 * Broadcast schedules — the cron strings the form writes, and the words the list shows for them.
 *
 * The engine hands a broadcast's 5-field cron to BullMQ, which resolves it with cron-parser, so a
 * cron is the whole contract. Kaze keeps the choice to a set of presets at 09:00 and maps every cron
 * it writes back to the preset that wrote it, so a user edits a schedule and never a cron string.
 *
 * Monthly follows OptiSigns (scio3 #2165 / #2147) exactly:
 *  - "On day N" is `0 9 N * *`. A literal day skips months that lack it (29 misses February most
 *    years, 31 misses five months) — cron has no "clamp to the last day", and RFC 5545 skips the
 *    same way — so the form says so instead of letting the broadcast go quiet in February.
 *  - "Last day of the month" is `0 9 L * *` (28/29 Feb, 30 or 31, whichever month runs).
 *  - "On the Nth weekday" is `0 9 * * D#N`, or `0 9 * * DL` for the last one.
 *  There is no "-1 = first day" sentinel: the first day is 1 (scio3 #2147 saved -1 and got a 400).
 */

/** Cron day-of-week values, Monday first as the pickers show them (cron: Sun=0 … Sat=6). */
export const WEEKDAYS = [
  { value: "1", label: "Monday", short: "Mon" },
  { value: "2", label: "Tuesday", short: "Tue" },
  { value: "3", label: "Wednesday", short: "Wed" },
  { value: "4", label: "Thursday", short: "Thu" },
  { value: "5", label: "Friday", short: "Fri" },
  { value: "6", label: "Saturday", short: "Sat" },
  { value: "0", label: "Sunday", short: "Sun" },
] as const;

/** The three ways the form can say "monthly". */
export type MonthlyMode = "dayOfMonth" | "lastDay" | "dayOfWeek";

/** Ordinal position of a weekday inside the month (`-1` = the last one). */
export const MONTH_WEEKS = [
  { value: "1", label: "First" },
  { value: "2", label: "Second" },
  { value: "3", label: "Third" },
  { value: "4", label: "Fourth" },
  { value: "-1", label: "Last" },
] as const;

export interface MonthlySchedule {
  mode: MonthlyMode;
  /** dayOfMonth: 1-31. */
  day: number;
  /** dayOfWeek: 1-4, or -1 for the last. */
  nth: number;
  /** dayOfWeek: cron weekday 0-6. */
  weekday: number;
}

export const DEFAULT_MONTHLY: MonthlySchedule = {
  mode: "dayOfMonth",
  day: 1,
  nth: 1,
  weekday: 1,
};

/** The hour every preset runs at. */
const PRESET_HOUR = 9;

/** Smallest day-of-month that does not exist in every month. */
const FIRST_SKIPPABLE_MONTH_DAY = 29;

/** Does a monthly schedule on this day silently miss some months? */
export function monthDaySkipsMonths(day: number): boolean {
  return Number.isInteger(day) && day >= FIRST_SKIPPABLE_MONTH_DAY;
}

/** A typed day-of-month made valid: a whole number from 1 to 31 (`1.5` → 1, `40` → 31, `` → 1). */
export function clampMonthDay(raw: string | number): number {
  const n = Math.trunc(Number(raw));
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(31, n);
}

/** The cron for a monthly schedule at 09:00. */
export function monthlyCron(schedule: MonthlySchedule): string {
  switch (schedule.mode) {
    case "lastDay":
      return `0 ${PRESET_HOUR} L * *`;
    case "dayOfWeek":
      return `0 ${PRESET_HOUR} * * ${schedule.weekday}${schedule.nth === -1 ? "L" : `#${schedule.nth}`}`;
    default:
      return `0 ${PRESET_HOUR} ${clampMonthDay(schedule.day)} * *`;
  }
}

const DAY_OF_MONTH_RE = /^0 (\d{1,2}) (\d{1,2}) \* \*$/;
const LAST_DAY_RE = /^0 (\d{1,2}) L \* \*$/;
const NTH_WEEKDAY_RE = /^0 (\d{1,2}) \* \* ([0-6])(?:#([1-4])|(L))$/;

/** A monthly cron recognised, with the hour it runs at. */
export type ParsedMonthlyCron = MonthlySchedule & { hour: number };

/**
 * Recognise a monthly cron (any hour), so the list can describe it and the form can reopen it in
 * the mode that wrote it. Anything else returns `null` — a hand-typed cron stays a hand-typed cron.
 */
export function parseMonthlyCron(
  cron: string | null | undefined,
): ParsedMonthlyCron | null {
  const raw = cron?.trim();
  if (!raw) return null;
  const dom = DAY_OF_MONTH_RE.exec(raw);
  if (dom && isHour(+dom[1]) && +dom[2] >= 1 && +dom[2] <= 31) {
    return {
      ...DEFAULT_MONTHLY,
      mode: "dayOfMonth",
      day: +dom[2],
      hour: +dom[1],
    };
  }
  const last = LAST_DAY_RE.exec(raw);
  if (last && isHour(+last[1])) {
    return { ...DEFAULT_MONTHLY, mode: "lastDay", hour: +last[1] };
  }
  const nth = NTH_WEEKDAY_RE.exec(raw);
  if (nth && isHour(+nth[1])) {
    return {
      ...DEFAULT_MONTHLY,
      mode: "dayOfWeek",
      weekday: +nth[2],
      nth: nth[4] ? -1 : +nth[3],
      hour: +nth[1],
    };
  }
  return null;
}

function isHour(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= 23;
}

/** `1` → `1st`, `2` → `2nd`, `-1` → `last`. */
export function ordinal(nth: number): string {
  if (nth === -1) return "last";
  return `${nth}${["th", "st", "nd", "rd"][nth] ?? "th"}`;
}

/** Cron weekday number → full day name. */
export function weekdayName(weekday: number): string {
  return WEEKDAYS.find((d) => d.value === String(weekday))?.label ?? "Monday";
}

/**
 * Tokens a Kaze cron may use: digits, `* , - /`, plus `L` and `#` for the monthly "last day" and
 * "Nth weekday" forms. The engine re-validates with a full parser; this only rejects free text and
 * 6-field (seconds) crons. Keep in sync with the API's `CRON_5_FIELD_REGEX`.
 */
export const CRON_5_FIELD_REGEX = /^[\d*,/#L-]+(?:\s+[\d*,/#L-]+){4}$/;
